import React from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, AlertCircle, CheckCircle2, Lock } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don\u2019t match.");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't reset your password. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-[#0A0A0A]/10 p-10 text-center">
          <AlertCircle size={32} className="text-[#B91C1C] mx-auto" />
          <h1 className="mt-4 font-display text-2xl font-black tracking-tight text-[#0A0A0A]">Missing reset token</h1>
          <p className="mt-3 text-sm text-[#475569]">
            This link looks incomplete. Please request a fresh reset email.
          </p>
          <Link to="/forgot-password" data-testid="reset-request-new-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1E4D55] hover:text-[#163C42]">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Link to="/login" data-testid="reset-back-to-login" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold text-[#475569] hover:text-[#0A0A0A] mb-8">
          <ArrowLeft size={12} /> Back to sign in
        </Link>

        <div className="bg-white border border-[#0A0A0A]/10 p-10">
          {!done ? (
            <>
              <h1 className="font-display text-3xl font-black tracking-tight text-[#0A0A0A]">Set a new password</h1>
              <p className="mt-3 text-sm text-[#475569] leading-relaxed">
                Choose something at least 8 characters. We&apos;ll sign you in after.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="reset-password-form">
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">
                    New password
                  </label>
                  <input
                    type="password" required minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    data-testid="reset-password-input"
                    className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">
                    Confirm new password
                  </label>
                  <input
                    type="password" required minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    data-testid="reset-confirm-input"
                    className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
                  />
                </div>

                {error && (
                  <div data-testid="reset-error" className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3.5 text-sm font-semibold transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                  {loading ? "Resetting…" : "Reset password"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="reset-done" className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#5BB247]/10 border border-[#5BB247]/40 mb-6">
                <CheckCircle2 size={28} className="text-[#2c8642]" />
              </div>
              <h1 className="font-display text-2xl font-black tracking-tight text-[#0A0A0A]">Password updated</h1>
              <p className="mt-3 text-sm text-[#475569]">
                Redirecting you to sign in…
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
